import React, { useState } from 'react';
import { Target, Zap, Users, ArrowRight, BookOpen, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { QuickDriverMode } from './QuickDriverMode';
import { OutcomeDriverSetBuilder } from './OutcomeDriverSetBuilder';
import { InstructionPanel } from './InstructionPanel';

type Mode = 'selection' | 'quick' | 'set-builder';

export const OutcomeDriverGenerator: React.FC = () => {
  const [mode, setMode] = useState<Mode>('selection');
  const [showInstructions, setShowInstructions] = useState(false);
  const [searchParams] = useSearchParams();

  // Get cycle context from URL params
  const cycleId = searchParams.get('cycleId');
  const cycleName = searchParams.get('cycleName');
  const returnUrl = cycleId ? `/prioritization/cycles/${cycleId}` : null;

  if (mode === 'quick') {
    return <QuickDriverMode onBack={() => setMode('selection')} cycleId={cycleId} cycleName={cycleName} returnUrl={returnUrl} />;
  }

  if (mode === 'set-builder') {
    return <OutcomeDriverSetBuilder onBack={() => setMode('selection')} cycleId={cycleId} cycleName={cycleName} returnUrl={returnUrl} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-charcoal-50 to-white">
      <div className="container mx-auto px-4 py-12">
        {/* Cycle Context Banner - only shown when launched from a cycle */}
        {returnUrl && (
          <div className="mb-6 bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--slate-dim)]">
                  <Target className="h-5 w-5 text-[var(--text-ter)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-sec)]">Creating drivers for: {cycleName || 'Untitled Cycle'}</p>
                  <p className="text-sm text-[var(--text-sec)]">Choose a mode below. When done, use "Save & Return" to add drivers to your cycle.</p>
                </div>
              </div>
              <Link
                to={returnUrl}
                className="inline-flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Cancel
              </Link>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-primary-100 px-6 py-3 mb-6">
            <Target className="h-6 w-6 text-primary-600" />
            <span className="font-semibold text-primary-800">Outcome Driver Generator</span>
          </div>
          <h1 className="text-4xl font-bold text-charcoal-900 mb-4">
            Create Compelling Reasons for Change
          </h1>
          <p className="text-xl text-charcoal-700 max-w-3xl mx-auto">
            Build well-structured Outcome Drivers that articulate why change is needed. 
            Choose your mode to get started.
          </p>
        </div>

        {/* Mode Selection */}
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Quick Driver Mode */}
            <div 
              onClick={() => setMode('quick')}
              className="group cursor-pointer bg-[var(--bg-raised)] border-2 border-primary-200 p-8 hover:border-primary-400 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary-100">
                  <Zap className="h-6 w-6 text-primary-600" />
                </div>
                <h2 className="text-2xl font-bold text-charcoal-900">Quick Driver</h2>
              </div>
              <p className="text-charcoal-700 mb-6">
                Create a single Outcome Driver quickly with guided coaching. Perfect for 
                individuals working through a specific issue or execs in solo reflection.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-primary-600" />
                  <span>15-30 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-primary-600" />
                  <span>Heavy on micro-instruction</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-primary-600" />
                  <span>Examples and feedback</span>
                </div>
              </div>
              <button className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 transition-colors flex items-center justify-center gap-2">
                Start Quick Driver
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Outcome Driver Set Builder */}
            <div 
              onClick={() => setMode('set-builder')}
              className="group cursor-pointer bg-[var(--bg-raised)] border-2 border-sage-200 p-8 hover:border-sage-400 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-sage-100">
                  <Users className="h-6 w-6 text-sage-600" />
                </div>
                <h2 className="text-2xl font-bold text-charcoal-900">Outcome Driver Set Builder</h2>
              </div>
              <p className="text-charcoal-700 mb-6">
                Build a complementary set of 1-5 drivers under a shared context. 
                Perfect for leadership teams in sprints, offsites, and workshops.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-sage-600" />
                  <span>Multiple drivers in shared context</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-sage-600" />
                  <span>Collaboration support</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-charcoal-600">
                  <ArrowRight className="h-4 w-4 text-sage-600" />
                  <span>Ordering and complementarity</span>
                </div>
              </div>
              <button className="w-full bg-sage-600 hover:bg-sage-700 text-white font-semibold py-3 px-6 transition-colors flex items-center justify-center gap-2">
                Start Set Builder
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Instructions Toggle */}
          <div className="mt-8">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 border border-primary-200 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary-600" />
                <span className="font-semibold text-charcoal-900">View Instructions</span>
              </div>
              {showInstructions ? (
                <ChevronUp className="h-5 w-5 text-charcoal-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-charcoal-600" />
              )}
            </button>
            {showInstructions && (
              <div className="mt-4">
                <InstructionPanel />
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-sage-50 p-8 border border-primary-200">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="h-5 w-5 text-primary-600" />
              <h3 className="text-xl font-bold text-charcoal-900">What is an Outcome Driver?</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              An Outcome Driver is a concise narrative answer to "Why bother doing anything different?" 
              It combines three essential elements:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-[var(--bg-raised)] p-4 border border-primary-200">
                <h4 className="font-semibold text-charcoal-900 mb-2">Current State</h4>
                <p className="text-sm text-charcoal-600">
                  A factual, objective description of how things are now.
                </p>
              </div>
              <div className="bg-[var(--bg-raised)] p-4 border border-primary-200">
                <h4 className="font-semibold text-charcoal-900 mb-2">Problem / Opportunity</h4>
                <p className="text-sm text-charcoal-600">
                  The core issue, tension, or opening in the current situation.
                </p>
              </div>
              <div className="bg-[var(--bg-raised)] p-4 border border-primary-200">
                <h4 className="font-semibold text-charcoal-900 mb-2">Ramifications / Value Judgment</h4>
                <p className="text-sm text-charcoal-600">
                  Why the current state matters enough to change—what's at stake if nothing changes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


